import { useQuery } from '@tanstack/react-query'

import axios from '@/_lib/axiosInstance'
import { ApiResponse, ITemplateTimeslot } from '@/_types'

/**
 * Frees/bookable timeslots for a template between two inclusive calendar dates (`yyyy-MM-dd`).
 *
 * @param id - Template id.
 * @param dateFrom - Range start sent as `dateFrom` query param.
 * @param dateTo - Range end sent as `dateTo` query param.
 */
export const fetchTimeSlots = async (
	id: number,
	dateFrom: string,
	dateTo: string
): Promise<ApiResponse<ITemplateTimeslot[]>> => {
	const queryParams = new URLSearchParams()
	queryParams.append('templateId', id.toString())
	queryParams.append('dateFrom', dateFrom)
	queryParams.append('dateTo', dateTo)

	return axios
		.get<ApiResponse<ITemplateTimeslot[]>>(
			`v1/web/ecommerce/timeslots?${queryParams.toString()}`
		)
		.then(res => res.data)
}

/**
 * React Query hook for {@link fetchTimeSlots} with optional `enabled` override (e.g. lazy calendar open).
 *
 * @param id - Template id.
 * @param dateFrom - Current week or window start.
 * @param dateTo - Current week or window end.
 * @param options - Query options; `enabled` defaults to `true` when omitted.
 */
export const useGetTimeSlots = (
	id: number,
	dateFrom: string,
	dateTo: string,
	options?: { enabled?: boolean }
) => {
	return useQuery<ApiResponse<ITemplateTimeslot[]>>({
		queryKey: ['time-slots', id, dateFrom, dateTo],
		queryFn: () => fetchTimeSlots(id, dateFrom, dateTo),
		select: data => data,
		enabled: options?.enabled ?? true
	})
}
